import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Spinner } from "@/components/ui-kit";
import MissingFieldsAlert from "@/components/MissingFieldsAlert";

import Login from "@/pages/Login";
import PinLogin from "@/pages/PinLogin";
import DirectAccess from "@/pages/DirectAccess";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordPages";

import ClientLayout from "@/pages/client/ClientLayout";
import ClientDashboard from "@/pages/client/ClientDashboard";
import ControlliOggi from "@/pages/client/ControlliOggi";
import CheckUp from "@/pages/client/CheckUp";
import Temperature from "@/pages/client/Temperature";
import OlioFrittura from "@/pages/client/OlioFrittura";
import ConformityRegistry from "@/pages/client/ConformityRegistry";
import NonConformita from "@/pages/client/NonConformita";
import Assistenza from "@/pages/client/Assistenza";
import Attrezzature from "@/pages/client/Attrezzature";
import MezziTrasporto from "@/pages/client/MezziTrasporto";
import Ricette from "@/pages/client/Ricette";
import Ingredienti from "@/pages/client/Ingredienti";
import Produzioni from "@/pages/client/Produzioni";
import ProgrammazioneProduzioni from "@/pages/client/ProgrammazioneProduzioni";
import Magazzino from "@/pages/client/Magazzino";
import SmartLotto from "@/pages/client/SmartLotto";
import Moca from "@/pages/client/Moca";
import Sanificanti from "@/pages/client/Sanificanti";
import DdtList from "@/pages/client/DdtList";
import DdtForm from "@/pages/client/DdtForm";
import CreaProduzione from "@/pages/client/CreaProduzione";
import LottiRicetta from "@/pages/client/LottiRicetta";
import EtichetteAbbattuti from "@/pages/client/EtichetteAbbattuti";
import Etichette from "@/pages/client/Etichette";
import Report from "@/pages/client/Report";
import LibroIngredienti from "@/pages/client/LibroIngredienti";
import StampaQr from "@/pages/client/StampaQr";
import QrProduzioniCelle from "@/pages/client/QrProduzioniCelle";
import QrMateriePrime from "@/pages/client/QrMateriePrime";
import ReportGiornaliero from "@/pages/client/ReportGiornaliero";
import ModulePlaceholder, { ModuleGate } from "@/pages/client/ModulePlaceholder";
import TracePublic from "@/pages/public/TracePublic";
import IngredientBookPublic from "@/pages/public/IngredientBookPublic";
import RawMaterialPublic from "@/pages/public/RawMaterialPublic";
import IntakeForm from "@/pages/public/IntakeForm";
import ClientDocsForm from "@/pages/public/ClientDocsForm";
import OfflineInspection from "@/pages/public/OfflineInspection";
import DatiAzienda from "@/pages/client/DatiAzienda";
import Ispezione from "@/pages/client/Ispezione";
import Tracciabilita from "@/pages/client/Tracciabilita";
import Ritiri, { RitiroNuovo, RitiroDettaglio } from "@/pages/client/Ritiri";
import Abbattimento from "@/pages/client/Abbattimento";
import ProdottiAbbattuti from "@/pages/client/ProdottiAbbattuti";
import ShelfLifeExpert from "@/pages/client/ShelfLifeExpert";
import ClientChangePassword from "@/pages/client/ClientChangePassword";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import TenantDetail from "@/pages/admin/TenantDetail";
import ChangePassword from "@/pages/admin/ChangePassword";
import Onboarding from "@/pages/admin/Onboarding";
import AuditLog from "@/pages/admin/AuditLog";
import ReportSerale from "@/pages/admin/ReportSerale";
import ShelfLifeRulesAdmin from "@/pages/admin/ShelfLifeRulesAdmin";
import AcademyAdmin from "@/pages/admin/AcademyAdmin";
import NutritionAdmin from "@/pages/admin/NutritionAdmin";
import SafeGuardAdmin from "@/pages/admin/SafeGuardAdmin";
import RecipeLibraryAdmin from "@/pages/admin/RecipeLibraryAdmin";

function Landing() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center"><Spinner /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "super_admin" ? "/admin" : "/app"} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <MissingFieldsAlert />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/pin" element={<PinLogin />} />
            <Route path="/accesso-diretto" element={<DirectAccess />} />
            <Route path="/forgot" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/t/:token" element={<TracePublic />} />
            <Route path="/vetrina/:token" element={<TracePublic fixed />} />
            <Route path="/ingredienti/:token" element={<IngredientBookPublic />} />
            <Route path="/mp/:token" element={<RawMaterialPublic />} />
            <Route path="/intake/:token" element={<IntakeForm />} />
            <Route path="/documenti/:token" element={<ClientDocsForm />} />
            <Route path="/ispezione-offline" element={<OfflineInspection />} />

            {/* Area cliente */}
            <Route path="/app" element={<ProtectedRoute roles={["titolare", "responsabile", "addetto", "ispettore"]}><ClientLayout /></ProtectedRoute>}>
              <Route index element={<ClientDashboard />} />
              <Route path="check-up" element={<CheckUp />} />
              <Route path="controlli" element={<ControlliOggi />} />
              <Route path="report-giornaliero/:notificationId" element={<ReportGiornaliero />} />
              <Route path="temperature" element={<ModuleGate modKey="temperature"><Temperature /></ModuleGate>} />
              <Route path="pulizie" element={<ModuleGate modKey="pulizie"><ConformityRegistry category="cleaning" /></ModuleGate>} />
              <Route path="altri-controlli" element={<ConformityRegistry category="other" />} />
              <Route path="registro-automezzi" element={<ModuleGate modKey="anagrafiche"><ConformityRegistry category="vehicles" /></ModuleGate>} />
              <Route path="olio-frittura" element={<ModuleGate modKey="temperature"><OlioFrittura /></ModuleGate>} />
              <Route path="abbattimento" element={<ModuleGate modKey="temperature"><Abbattimento /></ModuleGate>} />
              <Route path="prodotti-abbattuti" element={<ModuleGate modKey="temperature"><ProdottiAbbattuti /></ModuleGate>} />
              <Route path="non-conformita" element={<ModuleGate modKey="non_conformita"><NonConformita /></ModuleGate>} />
              <Route path="attrezzature" element={<ModuleGate modKey="anagrafiche"><Attrezzature /></ModuleGate>} />
              <Route path="mezzi-trasporto" element={<ModuleGate modKey="anagrafiche"><MezziTrasporto /></ModuleGate>} />
              <Route path="ricette" element={<ModuleGate modKey="ricette"><Ricette /></ModuleGate>} />
              <Route path="ingredienti" element={<ModuleGate modKey="materie_prime"><Ingredienti /></ModuleGate>} />
              <Route path="lotti" element={<ModuleGate modKey="materie_prime"><Ingredienti initialTab="lotti" /></ModuleGate>} />
              <Route path="carica-lotti" element={<ModuleGate modKey="materie_prime"><Ingredienti initialTab="carica-lotti" /></ModuleGate>} />
              <Route path="tracciabilita" element={<ModuleGate modKey="materie_prime"><Tracciabilita /></ModuleGate>} />
              <Route path="fornitori" element={<ModuleGate modKey="fornitori"><Ingredienti initialTab="fornitori" /></ModuleGate>} />
              <Route path="produzioni" element={<ModuleGate modKey="ricette"><Produzioni /></ModuleGate>} />
              <Route path="crea-produzione" element={<ModuleGate modKey="ricette"><CreaProduzione /></ModuleGate>} />
              <Route path="lotti-ricetta/:recipeId" element={<ModuleGate modKey="ricette"><LottiRicetta /></ModuleGate>} />
              <Route path="programmazione-produzioni" element={<ModuleGate modKey="programmazione_produzioni"><ProgrammazioneProduzioni /></ModuleGate>} />
              <Route path="magazzino" element={<ModuleGate modKey="magazzino"><Magazzino /></ModuleGate>} />
              <Route path="smart-lotto" element={<ModuleGate modKey="smart_lotto"><SmartLotto /></ModuleGate>} />
              <Route path="moca" element={<ModuleGate modKey="moca"><Moca /></ModuleGate>} />
              <Route path="sanificanti" element={<ModuleGate modKey="pulizie"><Sanificanti /></ModuleGate>} />
              <Route path="ddt" element={<ModuleGate modKey="ddt"><DdtList /></ModuleGate>} />
              <Route path="ddt/nuovo" element={<ModuleGate modKey="ddt"><DdtForm /></ModuleGate>} />
              <Route path="ddt/:id" element={<ModuleGate modKey="ddt"><DdtForm /></ModuleGate>} />
              <Route path="etichette-abbattuti" element={<ModuleGate modKey="temperature"><EtichetteAbbattuti /></ModuleGate>} />
              <Route path="etichette" element={<ModuleGate modKey="etichette"><Etichette /></ModuleGate>} />
              <Route path="report" element={<ModuleGate modKey="report_base"><Report /></ModuleGate>} />
              <Route path="libro-ingredienti" element={<ModuleGate modKey="report_base"><LibroIngredienti /></ModuleGate>} />
              <Route path="stampa-qr" element={<ModuleGate modKey="report_base"><StampaQr /></ModuleGate>} />
              <Route path="qr-produzioni-celle" element={<ModuleGate modKey="qr_code"><QrProduzioniCelle /></ModuleGate>} />
              <Route path="qr-materie-prime" element={<ModuleGate modKey="qr_code"><QrMateriePrime /></ModuleGate>} />
              <Route path="dati-azienda" element={<DatiAzienda />} />
              <Route path="ispezione" element={<Ispezione />} />
              <Route path="ritiri" element={<Ritiri />} />
              <Route path="ritiri/nuovo" element={<RitiroNuovo />} />
              <Route path="ritiri/:id" element={<RitiroDettaglio />} />
              <Route path="shelf-life" element={<ModuleGate modKey="shelf_life_expert"><ShelfLifeExpert /></ModuleGate>} />
              <Route path="cambia-password" element={<ClientChangePassword />} />
              <Route path="assistenza" element={<Assistenza />} />
              <Route path="m/:modKey" element={<ModulePlaceholder />} />
            </Route>

            {/* Area consulente */}
            <Route path="/admin" element={<ProtectedRoute roles={["super_admin"]}><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="onboarding/:tenantId" element={<Onboarding />} />
              <Route path="tenants/:id" element={<TenantDetail />} />
              <Route path="cambia-password" element={<ChangePassword />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="report-serale" element={<ReportSerale />} />
              <Route path="shelf-life-rules" element={<ShelfLifeRulesAdmin />} />
              <Route path="academy" element={<AcademyAdmin />} />
              <Route path="nutrition" element={<NutritionAdmin />} />
              <Route path="safeguard" element={<SafeGuardAdmin />} />
              <Route path="archivio-ricette" element={<RecipeLibraryAdmin />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
